import type { CoachingConfig } from "./config";

/**
 * The prices a coach (or, as the fallback, their club) can choose, in pence. Each
 * is a store product (`scripts/create-coaching-store-products.js` reads this list),
 * so add a price here only alongside its products on both stores and RevenueCat.
 */
export const PRICE_TIERS = [999, 1299, 1499, 1799, 1999] as const;
export type PriceTier = typeof PRICE_TIERS[number];

/** One consumable per tier, the same id in App Store Connect, Play Console and RevenueCat. */
export const COACHING_PRODUCT_PREFIX = "com.swanamotion.swana.coaching_review_";

export const isPriceTier = (v: unknown): v is PriceTier => typeof v === "number" && (PRICE_TIERS as readonly number[]).includes(v);

const tierIn = (holder: unknown): PriceTier | null => {
    if (typeof holder !== "object" || holder === null) return null;
    const tier = (holder as { priceTier?: unknown }).priceTier;
    return isPriceTier(tier) ? tier : null;
};

/**
 * What a review with this coach costs: the tier the coach chose, else the tier
 * their club set (`clubs/{clubId}.coaching.priceTier`), else the configured
 * default. A stored value that is not a tier is ignored rather than trusted.
 */
export function coachPricePence(coach: unknown, club: unknown, cfg: Pick<CoachingConfig, "priceDisplayPence">): number {
    const clubCoaching = typeof club === "object" && club !== null ? (club as { coaching?: unknown }).coaching : undefined;
    return tierIn(coach) ?? tierIn(clubCoaching) ?? cfg.priceDisplayPence;
}

/** The store product for a price, or null when the price is not a tier and so cannot be bought. */
export function productIdFor(pricePence: number): string | null {
    return isPriceTier(pricePence) ? `${COACHING_PRODUCT_PREFIX}${pricePence}` : null;
}

/** The price a store product id stands for, or null when it is not a coaching review product. */
export function tierOfProduct(productId: unknown): PriceTier | null {
    if (typeof productId !== "string" || !productId.startsWith(COACHING_PRODUCT_PREFIX)) return null;
    const n = Number(productId.slice(COACHING_PRODUCT_PREFIX.length));
    return isPriceTier(n) ? n : null;
}
